/**
 * Gift Card Cashback Display
 * Calcula e exibe 30% de cashback para produtos do tipo Gift Card
 * Injeta HTML dinamicamente após o app Gift Card Hero carregar
 */

class GiftCardCashback {
  constructor() {
    this.cashbackPercentage = 0.30; // 30% de cashback
    this.currentValue = 0;
    this.isUpdating = false;
    this.unsubscribers = [];
    this.cashbackContainer = null;
    this.cashbackValueElement = null;
    this.retryCount = 0;
    this.maxRetries = 10;

    this.init();
  }

  init() {
    // Aguarda carregamento da página
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Verifica se é um produto Gift Card
    const isGiftCard = this.isGiftCardProduct();

    if (!isGiftCard) {
      return;
    }

    // Tenta injetar o HTML após um delay inicial
    setTimeout(() => {
      this.injectCashbackElement();
    }, 1000);

    // Observa mudanças no DOM para re-injetar se necessário
    this.observeDOMChanges();

    // Usa o sistema de pub/sub do Shopify para escutar mudanças de variante
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      const variantUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
      this.unsubscribers.push(variantUnsubscriber);

      const optionUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleOptionChange.bind(this)
      );
      this.unsubscribers.push(optionUnsubscriber);
    }

    // Adiciona listeners para cliques em opções (fallback)
    document.addEventListener('click', (e) => {
      const target = e.target.closest('.sc-card-option__value') ||
                    e.target.closest('[data-value-option]') ||
                    e.target.closest('.product-option-value') ||
                    e.target.closest('input[type="radio"]');

      if (target) {
        setTimeout(() => {
          this.updateCashbackValue();
        }, 200);
      }
    }, { passive: true });
  }

  isGiftCardProduct() {
    // Verifica se é Gift Card através de múltiplos métodos

    // Método 1: Verifica meta tag
    const metaProductType = document.querySelector('meta[property="product:type"]');
    if (metaProductType && metaProductType.content.toLowerCase().includes('gift')) {
      return true;
    }

    // Método 2: Verifica URL
    if (window.location.pathname.includes('gift') || window.location.pathname.includes('vale')) {
      return true;
    }

    // Método 3: Verifica objeto global product
    if (typeof window.product !== 'undefined' && window.product.type) {
      if (window.product.type.toLowerCase().includes('gift')) {
        return true;
      }
    }

    // Método 4: Verifica presença do Gift Card Hero
    if (document.querySelector('[data-giftcard-hero]') ||
        document.querySelector('.giftcard-hero') ||
        document.querySelector('#giftcard-hero')) {
      return true;
    }

    return false;
  }

  observeDOMChanges() {
    // Observa mudanças no DOM para detectar quando o app Gift Card Hero termina de carregar
    const observer = new MutationObserver((mutations) => {
      // Verifica se o cashback ainda existe no DOM
      if (!document.contains(this.cashbackContainer)) {
        // Se foi removido, tenta reinjetar
        clearTimeout(this.reinjectionTimeout);
        this.reinjectionTimeout = setTimeout(() => {
          this.injectCashbackElement();
        }, 500);
      }
    });

    // Observa mudanças no body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  injectCashbackElement() {
    // Verifica se já existe
    if (this.cashbackContainer && document.contains(this.cashbackContainer)) {
      this.updateCashbackValue();
      return;
    }

    // Encontra onde injetar (abaixo do botão de adicionar ao carrinho)
    const targetElement = this.findTargetElement();

    if (!targetElement) {
      // Retry com backoff
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        const delay = Math.min(1000 * this.retryCount, 5000);
        setTimeout(() => {
          this.injectCashbackElement();
        }, delay);
      }
      return;
    }

    // Reset retry count
    this.retryCount = 0;

    // Cria o elemento HTML
    const cashbackHTML = this.createCashbackHTML();

    // Insere após o elemento target
    targetElement.insertAdjacentHTML('afterend', cashbackHTML);

    // Pega referências aos elementos
    this.cashbackContainer = document.querySelector('[data-giftcard-cashback]');
    if (this.cashbackContainer) {
      this.cashbackValueElement = this.cashbackContainer.querySelector('.giftcard-cashback__value');

      // Atualiza o valor após um pequeno delay
      setTimeout(() => {
        this.updateCashbackValue();
      }, 300);
    }
  }

  findTargetElement() {
    // Procura o botão de adicionar ao carrinho ou formulário
    const selectors = [
      '.product-form__submit',
      'button[type="submit"][name="add"]',
      '.single_add_to_cart_button',
      'form[action*="/cart/add"]',
      '.product-form__buttons',
      '[data-product-form]',
      '.shopify-payment-button',
      '#AddToCart',
      '.add-to-cart'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        // Se for um botão, retorna o container pai
        if (element.tagName === 'BUTTON') {
          return element.closest('.product-form__buttons') || element.parentElement;
        }
        return element;
      }
    }

    return null;
  }

  createCashbackHTML() {
    return `
      <style>
        .giftcard-cashback {
          display: none;
          align-items: center;
          gap: 16px;
          padding: 20px;
          margin-top: 16px;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          opacity: 0;
          transform: translateY(-10px);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        }

        .giftcard-cashback--visible {
          display: flex !important;
          opacity: 1;
          transform: translateY(0);
        }

        .giftcard-cashback__icon {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          backdrop-filter: blur(10px);
        }

        .giftcard-cashback__icon svg {
          width: 24px;
          height: 24px;
          stroke: #ffffff;
          animation: giftcard-cashback-pulse 2s ease-in-out infinite;
        }

        @keyframes giftcard-cashback-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        .giftcard-cashback__content {
          flex: 1;
          color: #ffffff;
        }

        .giftcard-cashback__text {
          margin: 0;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.3;
          letter-spacing: -0.02em;
          color: #ffffff;
        }

        .giftcard-cashback__value {
          font-size: 22px;
          font-weight: 700;
          color: #ffd700;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .giftcard-cashback__subtext {
          margin: 4px 0 0;
          font-size: 13px;
          opacity: 0.9;
          font-weight: 400;
          color: #ffffff;
        }

        @media screen and (max-width: 749px) {
          .giftcard-cashback {
            padding: 16px;
            gap: 12px;
          }

          .giftcard-cashback__icon {
            width: 40px;
            height: 40px;
          }

          .giftcard-cashback__icon svg {
            width: 20px;
            height: 20px;
          }

          .giftcard-cashback__text {
            font-size: 16px;
          }

          .giftcard-cashback__value {
            font-size: 19px;
          }

          .giftcard-cashback__subtext {
            font-size: 12px;
          }
        }
      </style>

      <div class="giftcard-cashback" data-giftcard-cashback>
        <div class="giftcard-cashback__icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2v20"></path>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            <path d="M23 12a11 11 0 0 1-11 11"></path>
            <path d="M1 12a11 11 0 0 1 11-11"></path>
            <polyline points="20 8 23 11 20 14"></polyline>
            <polyline points="4 16 1 13 4 10"></polyline>
          </svg>
        </div>
        <div class="giftcard-cashback__content">
          <p class="giftcard-cashback__text">
            Você ganha <strong class="giftcard-cashback__value">R$ 0,00</strong> de cashback!
          </p>
          <p class="giftcard-cashback__subtext">Válido para suas próximas compras</p>
        </div>
      </div>
    `;
  }

  handleVariantChange(event) {
    if (this.isUpdating) return;

    if (event && event.data && event.data.variant) {
      const variant = event.data.variant;
      if (variant.price) {
        const value = variant.price / 100;
        this.updateWithValue(value);
      }
    } else {
      this.updateCashbackValue();
    }
  }

  handleOptionChange() {
    if (this.isUpdating) return;

    setTimeout(() => {
      this.updateCashbackValue();
    }, 100);
  }

  updateCashbackValue() {
    if (this.isUpdating) return;
    if (!this.cashbackContainer) {
      this.injectCashbackElement();
      return;
    }

    this.isUpdating = true;

    try {
      const value = this.extractGiftCardValue();

      if (value > 0 && value !== this.currentValue) {
        this.currentValue = value;
        const cashbackAmount = value * this.cashbackPercentage;
        this.displayCashback(cashbackAmount);
      }
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 100);
    }
  }

  updateWithValue(value) {
    if (this.isUpdating) return;
    if (!this.cashbackContainer) {
      this.injectCashbackElement();
      return;
    }

    this.isUpdating = true;

    try {
      if (value > 0 && value !== this.currentValue) {
        this.currentValue = value;
        const cashbackAmount = value * this.cashbackPercentage;
        this.displayCashback(cashbackAmount);
      }
    } finally {
      setTimeout(() => {
        this.isUpdating = false;
      }, 100);
    }
  }

  extractGiftCardValue() {
    // Método 1: Tentar extrair do elemento .sc-card-option__value selecionado (Avis Options)
    const selectedOption = document.querySelector('.sc-card-option__value.selected') ||
                          document.querySelector('.sc-card-option__value.active') ||
                          document.querySelector('.sc-card-option__value[aria-checked="true"]') ||
                          document.querySelector('.sc-card-option__value.is-selected');

    if (selectedOption) {
      const valueText = selectedOption.textContent.trim();
      const numericValue = this.parseMoneyValue(valueText);
      if (numericValue > 0) {
        return numericValue;
      }
    }

    // Método 2: Tentar extrair do preço do produto selecionado
    const priceElement = document.querySelector('.price-item--regular .price-item__price') ||
                        document.querySelector('.product__price .price') ||
                        document.querySelector('[data-price]');

    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      const numericValue = this.parseMoneyValue(priceText);
      if (numericValue > 0) {
        return numericValue;
      }
    }

    // Método 3: Extrair do variant selecionado via input hidden
    const variantInput = document.querySelector('.product-variant-id') ||
                        document.querySelector('input[name="id"]');

    if (variantInput && variantInput.value) {
      const variantData = this.getVariantData(variantInput.value);
      if (variantData && variantData.price) {
        return variantData.price / 100;
      }
    }

    return 0;
  }

  parseMoneyValue(text) {
    const cleanText = text.replace(/[^\d.,]/g, '');
    const normalizedText = cleanText.replace(',', '.');
    const value = parseFloat(normalizedText);
    return isNaN(value) ? 0 : value;
  }

  getVariantData(variantId) {
    if (typeof window.product !== 'undefined' && window.product.variants) {
      const variant = window.product.variants.find(v => v.id == variantId);
      return variant;
    }
    return null;
  }

  displayCashback(amount) {
    if (!this.cashbackValueElement || !this.cashbackContainer) {
      return;
    }

    const formattedAmount = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);

    this.cashbackValueElement.textContent = formattedAmount;

    if (!this.cashbackContainer.classList.contains('giftcard-cashback--visible')) {
      this.cashbackContainer.offsetHeight; // Force reflow
      this.cashbackContainer.classList.add('giftcard-cashback--visible');
    }
  }

  destroy() {
    this.unsubscribers.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    this.unsubscribers = [];

    if (this.cashbackContainer && document.contains(this.cashbackContainer)) {
      this.cashbackContainer.remove();
    }
  }
}

// Inicializa
let giftCardCashbackInstance = null;

function initGiftCardCashback() {
  if (giftCardCashbackInstance) {
    giftCardCashbackInstance.destroy();
  }
  giftCardCashbackInstance = new GiftCardCashback();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftCardCashback);
} else {
  initGiftCardCashback();
}
